import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import { categories, getHotMovies, getNewMovies, getRecommendedMovies, movies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

const heroMovies = movies.filter((m) => m.isHot).slice(0, 3);

export default function Home() {
  const [currentHero, setCurrentHero] = useState(0);

  const nextHero = useCallback(() => {
    setCurrentHero((prev) => (prev + 1) % heroMovies.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextHero, 5000);
    return () => clearInterval(timer);
  }, [nextHero]);

  const hotMovies = getHotMovies();
  const newMovies = getNewMovies();
  const recommendedMovies = getRecommendedMovies();

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          {heroMovies.map((movie, index) => (
            <div
              key={movie.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentHero ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={movie.backdropUrl}
                alt={movie.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-background-950" />
            </div>
          ))}

          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-4">
            <div className="w-full max-w-3xl">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3 leading-tight">
                {heroMovies[currentHero].title}
              </h1>
              <p className="text-sm md:text-base text-gray-300 mb-2 max-w-xl mx-auto leading-relaxed">
                {heroMovies[currentHero].description}
              </p>
              <div className="flex items-center justify-center gap-3 mt-3 text-xs md:text-sm text-gray-400">
                <span className="bg-primary-500/80 text-white px-2 py-0.5 rounded-md text-xs">{heroMovies[currentHero].rating}分</span>
                <span>{heroMovies[currentHero].year}</span>
                <span>{heroMovies[currentHero].type === 'tv' ? heroMovies[currentHero].duration : heroMovies[currentHero].duration}</span>
              </div>
              <Link
                to={`/article/${heroMovies[currentHero].id}`}
                className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i>
                立即观看
              </Link>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {heroMovies.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentHero(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentHero ? 'bg-primary-500 w-6' : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`切换到第${index + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="bg-background-950 border-b border-background-800">
          <div className="w-full px-4 md:px-6 py-4">
            <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 bg-background-800 text-foreground-400 hover:bg-accent-500/20 hover:text-accent-400 whitespace-nowrap cursor-pointer shrink-0"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  <span>{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Movies */}
        <section className="py-10 md:py-14 bg-background-950">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-primary-500 rounded-full" />
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100">
                  热门推荐
                </h2>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-foreground-500 hover:text-primary-400 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="flex gap-4 md:gap-5 overflow-x-auto pb-4" data-product-shop>
              {hotMovies.map((movie) => (
                <div key={movie.id} className="shrink-0">
                  <MovieCard movie={movie} size="md" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recommended Section */}
        <section className="py-10 md:py-14 bg-background-900/50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-accent-500 rounded-full" />
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100">
                  好评如潮
                </h2>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-foreground-500 hover:text-primary-400 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {recommendedMovies.slice(0, 12).map((movie) => (
                <MovieCard key={movie.id} movie={movie} size="md" />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Movies */}
        <section className="py-10 md:py-14 bg-background-950">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-secondary-500 rounded-full" />
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100">
                  最新上线
                </h2>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-foreground-500 hover:text-primary-400 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {newMovies.slice(0, 12).map((movie) => (
                <MovieCard key={movie.id} movie={movie} size="md" />
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-10 md:py-14 bg-background-900/30">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-2">
                为什么选择七七影院
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                我们致力于为您提供最佳的在线观影体验
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: 'ri-4k-line', title: '超清画质', desc: '4K高清画质，沉浸式观影体验' },
                { icon: 'ri-flashlight-line', title: '极速加载', desc: '智能CDN加速，即点即看零等待' },
                { icon: 'ri-shield-check-line', title: '免费无广告', desc: '纯净观影，无任何广告打扰' },
                { icon: 'ri-refresh-line', title: '每日更新', desc: '最新影视资源每日同步上线' },
              ].map((feature, i) => (
                <div key={i} className="bg-background-800/50 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-500/10 rounded-full">
                    <i className={`${feature.icon} text-accent-500 text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-200 mb-1">{feature.title}</h3>
                  <p className="text-xs text-foreground-500">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-10 md:py-14 bg-background-950">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-2">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于七七影院的常见疑问解答</p>
            </div>
            <div className="flex flex-col gap-3">
              {[
                { q: '七七影院真的免费吗？', a: '是的，七七影院所有影视资源均可免费在线观看，无需付费，无需会员注册，即点即看。' },
                { q: '需要下载播放器吗？', a: '不需要。七七影院支持网页直接播放，兼容电脑、手机、平板等多种设备，无需安装任何插件或播放器。' },
                { q: '画质怎么样？会缓冲吗？', a: '我们提供超清画质资源，并采用智能CDN加速技术，确保流畅播放不缓冲。实际画质取决于您的网络环境。' },
                { q: '资源多久更新一次？', a: '我们每日更新最新影视资源，热门电影、电视剧上线后第一时间为您同步，确保您不会错过任何精彩内容。' },
                { q: '支持哪些设备观看？', a: '七七影院支持电脑、平板、手机等所有主流设备。只需浏览器即可访问，随时随地畅享高清观影体验。' },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-800/50 rounded-lg overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between px-5 py-3 md:py-4 text-sm font-medium text-foreground-200 list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '七七影院',
            url: typeof window !== 'undefined' ? window.location.origin : '',
            description: '七七影院提供海量高清免费电影与免费电视剧在线看，画质超清不缓冲，无需付费即点即看。涵盖热播剧、经典电影大全，每日更新最新资源。',
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: typeof window !== 'undefined' ? `${window.location.origin}/category/all?q={search_term_string}` : '',
              },
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '七七影院真的免费吗？', acceptedAnswer: { '@type': 'Answer', text: '是的，七七影院所有影视资源均可免费在线观看，无需付费，无需会员注册，即点即看。' } },
              { '@type': 'Question', name: '需要下载播放器吗？', acceptedAnswer: { '@type': 'Answer', text: '不需要。七七影院支持网页直接播放，兼容电脑、手机、平板等多种设备，无需安装任何插件或播放器。' } },
              { '@type': 'Question', name: '画质怎么样？会缓冲吗？', acceptedAnswer: { '@type': 'Answer', text: '我们提供超清画质资源，并采用智能CDN加速技术，确保流畅播放不缓冲。' } },
              { '@type': 'Question', name: '资源多久更新一次？', acceptedAnswer: { '@type': 'Answer', text: '我们每日更新最新影视资源，热门电影、电视剧上线后第一时间为您同步。' } },
              { '@type': 'Question', name: '支持哪些设备观看？', acceptedAnswer: { '@type': 'Answer', text: '七七影院支持电脑、平板、手机等所有主流设备。只需浏览器即可访问。' } },
            ],
          }),
        }}
      />
    </>
  );
}